// src/components/billing/AdminPricingPanel.tsx
/**
 * Admin Pricing Panel — superuser-only configuration for model costs & margin.
 *
 * Displays:
 *   - Model cost inputs (LLM per 1K tokens, Voice per minute)
 *   - Margin multiplier input
 *   - Auto-computed customer prices & margin percentage
 *
 * Uses the Athena brand identity (glassmorphic cards, gradient accents).
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Alert,
  Skeleton,
  Divider,
  Chip,
  useTheme,
  alpha,
  InputAdornment,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import TuneIcon from "@mui/icons-material/Tune";
import EuroIcon from "@mui/icons-material/Euro";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import PercentIcon from "@mui/icons-material/Percent";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "../../i18n";
import api from "../../api/api";
import { athenaColors } from "../../theme/palette";
import { glass } from "../../theme/glassTokens";

interface PricingData {
  configs: Record<
    string,
    { value: string; description: string; updated_at: string | null }
  >;
  margin_multiplier: number;
  margin_percent: number;
  model_cost_llm_per_1k: number;
  model_cost_voice_per_minute: number;
  customer_token_price_per_1k: number;
  customer_voice_price_per_minute: number;
  currency: string;
}

const AdminPricingPanel: React.FC = () => {
  const theme = useTheme();
  const textPrimary = glass.textPrimary;
  const textSecondary = glass.textSecondary;
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [data, setData] = useState<PricingData | null>(null);

  // Editable fields
  const [marginMultiplier, setMarginMultiplier] = useState("");
  const [llmCost, setLlmCost] = useState("");
  const [voiceCost, setVoiceCost] = useState("");

  const fetchPricing = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/billing/admin/pricing/");
      const d: PricingData = response.data;
      setData(d);
      setMarginMultiplier(String(d.margin_multiplier));
      setLlmCost(String(d.model_cost_llm_per_1k));
      setVoiceCost(String(d.model_cost_voice_per_minute));
    } catch (err: any) {
      const msg =
        err.response?.data?.detail || "Failed to load pricing configuration.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPricing();
  }, [fetchPricing]);

  // Live-computed preview
  const marginNum = parseFloat(marginMultiplier) || 0;
  const llmCostNum = parseFloat(llmCost) || 0;
  const voiceCostNum = parseFloat(voiceCost) || 0;
  const customerTokenPrice =
    marginNum > 0 && llmCostNum > 0 ? llmCostNum * marginNum : 0;
  const customerVoicePrice =
    marginNum > 0 && voiceCostNum > 0 ? voiceCostNum * marginNum : 0;
  const marginPercent = marginNum > 1 ? (1 - 1 / marginNum) * 100 : 0;

  const hasChanges =
    data &&
    (String(data.margin_multiplier) !== marginMultiplier ||
      String(data.model_cost_llm_per_1k) !== llmCost ||
      String(data.model_cost_voice_per_minute) !== voiceCost);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload: Record<string, number> = {};
      if (data && String(data.margin_multiplier) !== marginMultiplier) {
        payload.margin_multiplier = parseFloat(marginMultiplier);
      }
      if (data && String(data.model_cost_llm_per_1k) !== llmCost) {
        payload.model_cost_llm_per_1k = parseFloat(llmCost);
      }
      if (data && String(data.model_cost_voice_per_minute) !== voiceCost) {
        payload.model_cost_voice_per_minute = parseFloat(voiceCost);
      }

      if (Object.keys(payload).length === 0) return;

      const response = await api.patch("/billing/admin/pricing/update/", payload);
      const d: PricingData = response.data;
      setData(d);
      setMarginMultiplier(String(d.margin_multiplier));
      setLlmCost(String(d.model_cost_llm_per_1k));
      setVoiceCost(String(d.model_cost_voice_per_minute));
      setSuccess(
        t(
          "billing.admin_pricing.saved",
          "Pricing updated successfully. New prices apply to all future usage."
        )
      );
    } catch (err: any) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.errors
          ? JSON.stringify(err.response?.data?.errors)
          : "Failed to save pricing changes.";
      setError(String(msg));
    } finally {
      setSaving(false);
    }
  }, [data, marginMultiplier, llmCost, voiceCost, t]);

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2, mb: 2 }} />
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={fetchPricing} size="small" sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${athenaColors.deepNavy}, ${athenaColors.royalBlue})`,
              display: "flex",
              alignItems: "center",
            }}
          >
            <TuneIcon sx={{ color: theme.palette.common.white, fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: textPrimary }}>
              {t("billing.admin_pricing.title", "Pricing Configuration")}
            </Typography>
            <Typography variant="caption" sx={{ color: textSecondary }}>
              {t(
                "billing.admin_pricing.subtitle",
                "Configure model costs and margin multiplier. Changes apply to all future usage calculations."
              )}
            </Typography>
          </Box>
        </Box>
        <Chip
          label={t("billing.admin_pricing.superuser_only", "Super Admin Only")}
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: "0.7rem",
            backgroundColor: alpha(athenaColors.royalBlue, 0.12),
            color: glass.accent,
            border: `1px solid ${alpha(athenaColors.royalBlue, 0.3)}`,
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left: Model Costs */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              height: "100%",
              p: { xs: 1.5, sm: 2.5 },
              borderRadius: 2,
              background: glass.panel,
              border: `1px solid ${glass.panelBorder}`,
              backdropFilter: "blur(8px)",
              transition: "border-color 0.2s ease",
              "&:hover": { borderColor: glass.headerDivider },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
              <PrecisionManufacturingIcon
                sx={{ color: athenaColors.blueGray, fontSize: 20 }}
              />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: textPrimary }}>
                {t("billing.admin_pricing.model_costs", "Model Costs")}
              </Typography>
              <Tooltip
                title={t(
                  "billing.admin_pricing.model_costs_help",
                  "The actual cost Azure charges you per unit. Update these when Azure pricing changes."
                )}
              >
                <InfoOutlinedIcon
                  sx={{ fontSize: 16, color: textSecondary, cursor: "help" }}
                />
              </Tooltip>
            </Box>

            <Typography
              variant="overline"
              sx={{ color: textSecondary, letterSpacing: 1.1, mb: 0.5, display: "block" }}
            >
              {t("billing.admin_pricing.llm_cost_label", "LLM Cost per 1K Tokens")}
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              value={llmCost}
              onChange={(e) => setLlmCost(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EuroIcon sx={{ fontSize: 16, color: textSecondary }} />
                  </InputAdornment>
                ),
              }}
              inputProps={{ step: "0.0001", min: "0" }}
              helperText={
                data?.configs?.model_cost_llm_per_1k?.description ||
                t(
                  "billing.admin_pricing.llm_cost_help",
                  "Azure OpenAI blended cost (input+output) per 1K tokens."
                )
              }
              sx={{ mb: 2.5 }}
            />

            <Typography
              variant="overline"
              sx={{ color: textSecondary, letterSpacing: 1.1, mb: 0.5, display: "block" }}
            >
              {t("billing.admin_pricing.voice_cost_label", "Voice Transcription Cost per Minute")}
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              value={voiceCost}
              onChange={(e) => setVoiceCost(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EuroIcon sx={{ fontSize: 16, color: textSecondary }} />
                  </InputAdornment>
                ),
              }}
              inputProps={{ step: "0.001", min: "0" }}
              helperText={
                data?.configs?.model_cost_voice_per_minute?.description ||
                t(
                  "billing.admin_pricing.voice_cost_help",
                  "Azure Whisper cost per minute of transcription."
                )
              }
            />
          </Box>
        </Grid>

        {/* Right: Margin & Customer Price */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              height: "100%",
              p: { xs: 1.5, sm: 2.5 },
              borderRadius: 2,
              background: glass.panel,
              border: `1px solid ${glass.panelBorder}`,
              backdropFilter: "blur(8px)",
              transition: "border-color 0.2s ease",
              "&:hover": { borderColor: glass.headerDivider },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
              <PercentIcon sx={{ color: athenaColors.blueGray, fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: textPrimary }}>
                {t("billing.admin_pricing.margin_section", "Margin & Customer Pricing")}
              </Typography>
            </Box>

            <Typography
              variant="overline"
              sx={{ color: textSecondary, letterSpacing: 1.1, mb: 0.5, display: "block" }}
            >
              {t("billing.admin_pricing.margin_label", "Margin Multiplier")}
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              value={marginMultiplier}
              onChange={(e) => setMarginMultiplier(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="caption" sx={{ color: textSecondary }}>
                      ×
                    </Typography>
                  </InputAdornment>
                ),
              }}
              inputProps={{ step: "1", min: "1" }}
              helperText={t(
                "billing.admin_pricing.margin_help",
                "Customer price = Model cost × Margin multiplier"
              )}
              sx={{ mb: 3 }}
            />

            <Divider sx={{ mb: 2.5 }} />

            {/* Computed results */}
            <Typography
              variant="overline"
              sx={{
                color: textSecondary,
                letterSpacing: 1.1,
                mb: 1.5,
                display: "block",
              }}
            >
              {t("billing.admin_pricing.computed_title", "Computed Customer Prices")}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    background: glass.panel,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="caption" sx={{ color: textSecondary }} display="block">
                    {t("billing.admin_pricing.token_price", "Token Price / 1K")}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "success.main" }}>
                    €{customerTokenPrice.toFixed(4)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    background: glass.panel,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="caption" sx={{ color: textSecondary }} display="block">
                    {t("billing.admin_pricing.voice_price", "Voice / min")}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "success.main" }}>
                    €{customerVoicePrice.toFixed(4)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    background: glass.panel,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="caption" sx={{ color: textSecondary }} display="block">
                    {t("billing.admin_pricing.margin_pct", "Margin Percentage")}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: marginPercent > 90 ? "success.main" : marginPercent > 50 ? "warning.main" : "error.main",
                    }}
                  >
                    {marginPercent.toFixed(2)}%
                  </Typography>
                  <Typography variant="caption" sx={{ color: textSecondary }}>
                    {t(
                      "billing.admin_pricing.margin_explanation",
                      "You keep this percentage of every euro charged."
                    )}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>

      {/* Save Button */}
      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || !hasChanges}
          sx={{
            px: 4,
            py: 1.2,
            fontWeight: 700,
            borderRadius: 2,
            background: hasChanges
              ? `linear-gradient(135deg, ${athenaColors.deepNavy}, ${athenaColors.royalBlue})`
              : undefined,
            "&:hover": hasChanges
              ? {
                  background: `linear-gradient(135deg, ${athenaColors.royalBlue}, ${athenaColors.deepNavy})`,
                }
              : undefined,
          }}
        >
          {saving
            ? t("billing.admin_pricing.saving", "Saving...")
            : t("billing.admin_pricing.save", "Save Changes")}
        </Button>
      </Box>

      {/* Info: last updated */}
      {data?.configs?.margin_multiplier?.updated_at && (
        <Typography variant="caption" sx={{ display: "block", mt: 1, textAlign: "right", color: textSecondary }}>
          {t("billing.admin_pricing.last_updated", "Last updated")}:{" "}
          {new Date(data.configs.margin_multiplier.updated_at).toLocaleString()}
        </Typography>
      )}
    </Box>
  );
};

export default AdminPricingPanel;
